import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Shield } from "lucide-react";

// Dati legali del titolare del trattamento.
const COMPANY = {
  name: "Italian Mood",
  vat: "IT02234750442",
  cf: "LCDGLC74L24H321Z",
  address: "Via Gabriele d'Annunzio 28, 63074 San Benedetto del Tronto (AP)",
  phone: "0735 593839",
  email: "info@italian-mood.it",
  privacyEmail: "privacy@archi-hub.it",
};

// Interpola i placeholder {{var}} in una stringa raw HTML (le sezioni legali sono
// testi admin-controlled, non input utente, quindi possiamo usare HTML diretto).
function tplHtml(raw, vars) {
  return Object.entries(vars || {}).reduce(
    (acc, [k, v]) => acc.replace(new RegExp(`{{\\s*${k}\\s*}}`, "g"), String(v)),
    raw
  );
}

function Html({ html }) {
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function PrivacyPolicy() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language.startsWith("it") ? "it" : "en";
  return (
    <div className="min-h-[100svh] pt-32 pb-20 bg-cream" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-[11px] tracking-[0.3em] uppercase text-slate1 inline-flex items-center gap-2"><Shield size={12} /> {t("legal.privacy.pre")}</div>
        <h1 className="mt-4 font-display text-4xl lg:text-5xl font-light text-ink">{t("legal.privacy.title")}</h1>
        <p className="mt-3 text-sm text-slate1">{t("legal.privacy.updated")}</p>

        <div className="prose prose-slate max-w-none mt-10 space-y-6 text-ink/90 text-[15px] leading-relaxed">
          <p><Html html={t("legal.privacy.intro")} /></p>

          <section>
            <h2 className="font-display text-2xl text-ink mt-8">{t("legal.privacy.h1")}</h2>
            <p>
              {COMPANY.name}<br />
              <Html html={tplHtml(t("legal.privacy.h1_body"), {
                address: COMPANY.address,
                vat: COMPANY.vat,
                cf: COMPANY.cf,
                phone: COMPANY.phone,
                email: COMPANY.email,
                privacyEmail: COMPANY.privacyEmail,
              })} />
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl text-ink mt-8">{t("legal.privacy.h2")}</h2>
            <p>{t("legal.privacy.h2_intro")}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><Html html={t("legal.privacy.h2_li1")} /></li>
              <li><Html html={t("legal.privacy.h2_li2")} /></li>
              <li><Html html={t("legal.privacy.h2_li3")} /></li>
              <li><Html html={t("legal.privacy.h2_li4")} /></li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl text-ink mt-8">{t("legal.privacy.h3")}</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><Html html={t("legal.privacy.h3_li1")} /></li>
              <li><Html html={t("legal.privacy.h3_li2")} /></li>
              <li><Html html={t("legal.privacy.h3_li3")} /></li>
              <li><Html html={t("legal.privacy.h3_li4")} /></li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl text-ink mt-8">{t("legal.privacy.h4")}</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t("legal.privacy.h4_li1")}</li>
              <li>{t("legal.privacy.h4_li2")}</li>
              <li>{t("legal.privacy.h4_li3")}</li>
              <li>{t("legal.privacy.h4_li4")}</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl text-ink mt-8">{t("legal.privacy.h5")}</h2>
            <p>{t("legal.privacy.h5_intro")}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t("legal.privacy.h5_li1")}</li>
              <li>{t("legal.privacy.h5_li2")}</li>
              <li>{t("legal.privacy.h5_li3")}</li>
            </ul>
            <p>{t("legal.privacy.h5_outro")}</p>
          </section>

          <section>
            <h2 className="font-display text-2xl text-ink mt-8">{t("legal.privacy.h6")}</h2>
            <p>{t("legal.privacy.h6_intro")}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t("legal.privacy.h6_li1")}</li>
              <li>{t("legal.privacy.h6_li2")}</li>
              <li>{t("legal.privacy.h6_li3")}</li>
              <li>{t("legal.privacy.h6_li4")}</li>
              <li>{t("legal.privacy.h6_li5")}</li>
              <li><Html html={t("legal.privacy.h6_li6")} /></li>
            </ul>
            <p><Html html={tplHtml(t("legal.privacy.h6_outro"), { privacyEmail: COMPANY.privacyEmail })} /></p>
          </section>

          <section>
            <h2 className="font-display text-2xl text-ink mt-8">{t("legal.privacy.h7")}</h2>
            <p>{t("legal.privacy.h7_body")}</p>
          </section>

          <section>
            <h2 className="font-display text-2xl text-ink mt-8">{t("legal.privacy.h8")}</h2>
            <p>
              {/* Mantiene la navigazione interna come React Link */}
              {t("legal.privacy.h8_body").split("{{cookieLink}}")[0]}
              <Link to="/cookie-policy" className="underline">{t("footer.cookies")}</Link>
              {t("legal.privacy.h8_body").split("{{cookieLink}}")[1] || ""}
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl text-ink mt-8">{t("legal.privacy.h9")}</h2>
            <p>{t("legal.privacy.h9_body")}</p>
          </section>

          {lng === "en" && (
            <p className="text-xs italic text-slate1 pt-6 border-t border-ink/10 mt-10">{t("legal.privacy.note")}</p>
          )}
        </div>

        <div className="mt-12 text-center">
          <Link to="/" className="inline-flex items-center gap-2 bg-ink text-cream px-6 py-3 text-[12px] tracking-[0.2em] uppercase rounded-sm" data-testid="privacy-back-home">
            {t("legal.back_home")}
          </Link>
        </div>
      </div>
    </div>
  );
}
